import React from 'react';
import { useClaimRequest } from '../hooks/usePayment';
import { useStandardClaimRequest } from '../hooks/useStandardClaimRequest';
import { useTelcoPaymentMethods } from '../hooks/usePayment';
import { useInstantClaimRequest } from '../hooks/useInstantClaimRequest';
import { useRefundClaimRequest } from '../hooks/useRefundClaimRequest';
import { useWithdrawalReasons } from '../hooks/useWithdrawalReasons';
import { useCheckClaimEligibility } from '../hooks/useCheckClaimEligibility';
import { usePensionsSummary } from '../hooks/dashboard/usePensionSummary';
import { usePolicySummary } from '../hooks/dashboard/usePolicySummary';
import { notifyUser, normalizeGhanaPhone, validateGhanaPhone } from '../util/util';
import { useModalWidth } from '../hooks/useModalWidth';
import { ChevronRight, Check } from 'lucide-react';

const toTitleCase = (str) => str ? str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : '';

// Step definitions
const STEPS = {
  SELECT_TYPE: 'select_type',
  SELECT_CLAIM_METHOD: 'select_claim_method',
  INSTANT_CLAIM_AMOUNT: 'instant_claim_amount',
  STANDARD_CLAIM_AMOUNT: 'standard_claim_amount',
  PAYMENT_METHOD: 'payment_method',
  MOBILE_MONEY_DETAILS: 'Mobile Money Details',
  BANK_PAYMENT_DETAILS: 'Bank Payment Details',
  REFUND_REQUEST: 'refund_request',
  REFUND_REVIEW: 'refund_review',
  SUMMARY: 'summary',
  SUCCESS: 'success',
  INELIGIBLE: 'ineligible',
};

const ClaimsModal = ({ isOpen, onClose, policyNumber, productType, policyName, availableCashValue }) => {
  // eslint-disable-next-line no-unused-vars
  const modalWidth = useModalWidth(); // Kept for future responsive use
  const [currentStep, setCurrentStep] = React.useState(STEPS.SELECT_TYPE);
  const [claimType, setClaimType] = React.useState(null); // 'partial_withdrawal' | 'refund_claim'
  const [claimMethod, setClaimMethod] = React.useState(null); // 'instant' | 'standard'
  const [claimAmount, setClaimAmount] = React.useState('');
  const [telecomMethod, setTelecomMethod] = React.useState('');
  const [momoWallet, setMomoWallet] = React.useState('');
  const [amountError, setAmountError] = React.useState('');
  const [phoneError, setPhoneError] = React.useState('');
  const [claimResponse, setClaimResponse] = React.useState(null);
  const [ineligibilityMessage, setIneligibilityMessage] = React.useState('');
  
  // Standard claim - payment method and bank details
  const [paymentMethod, setPaymentMethod] = React.useState(null); // 'Mobile Money' | 'Bank'
  const [selectedBank, setSelectedBank] = React.useState('');
  const [selectedBranch, setSelectedBranch] = React.useState('');
  const [accountNumber, setAccountNumber] = React.useState('');
  const [accountName, setAccountName] = React.useState('');
  
  // Refund claim details
  const [refundReason, setRefundReason] = React.useState('');
  
  // Instant claim - withdrawal purpose
  const [withdrawalPurpose, setWithdrawalPurpose] = React.useState('');

  const claimRequestMutation = useClaimRequest();
  const instantClaimMutation = useInstantClaimRequest();
  const refundClaimMutation = useRefundClaimRequest();
  const standardClaimMutation = useStandardClaimRequest();
  const checkEligibilityMutation = useCheckClaimEligibility();
  const { data: telcoMethodsData, isLoading: isLoadingTelcoMethods } = useTelcoPaymentMethods();
  const { data: withdrawalReasonsData, isLoading: isLoadingWithdrawalReasons } = useWithdrawalReasons();
  const { data: pensionsSummaryData } = usePensionsSummary();
  const { data: policySummaryData } = usePolicySummary();

  // Determine policy type from prop
  const isPensionPolicy = productType === 'PENSION';
  const isLifePolicy = productType === 'LIFE';

  // Get cash value - use prop if provided (including 0), otherwise calculate from hooks
  let currentCashValue = availableCashValue !== undefined ? availableCashValue : 0;
  if (availableCashValue === undefined || availableCashValue === null) {
    if (isPensionPolicy) {
      currentCashValue = policySummaryData?.data?.total_available_balance || 
                        pensionsSummaryData?.data?.total_investment || 0;
    } else if (isLifePolicy) {
      currentCashValue = policySummaryData?.data?.total_life_investment || 0;
    } else {
      currentCashValue = policySummaryData?.data?.total_available_balance || 
                        pensionsSummaryData?.data?.total_investment || 0;
    }
  }

  const telcoMethods = React.useMemo(() => telcoMethodsData?.data || [], [telcoMethodsData]);

  // Set default telecom method when data loads
  React.useEffect(() => {
    if (telcoMethods.length > 0 && !telecomMethod) {
      // Default to Vodafone (00465) or first available
      const vodafone = telcoMethods.find(m => m.emp_code === '00465');
      setTelecomMethod(vodafone?.emp_code || telcoMethods[0]?.emp_code);
    }
  }, [telcoMethods, telecomMethod]);

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setCurrentStep(STEPS.SELECT_TYPE);
      setClaimType(null);
      setClaimMethod(null);
      setClaimAmount('');
      setMomoWallet('');
      setAmountError('');
      setPhoneError('');
      setClaimResponse(null);
      setPaymentMethod(null);
      setSelectedBank('');
      setSelectedBranch('');
      setAccountNumber('');
      setAccountName('');
      setRefundReason('');
      setWithdrawalPurpose('');
    }
  }, [isOpen]);

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'GHS 0.00';
    const num = Number(value);
    return `GHS ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate max claimable - instant is 40% (up to GH₵3,000), standard is 50% (up to GH₵5,000)
  const getMaxClaimable = () => {
    const percentage = claimMethod === 'standard' ? 0.5 : 0.4;
    const maxCap = claimMethod === 'standard' ? 5000 : 3000;
    const calculated = currentCashValue * percentage;
    return Math.min(calculated, maxCap);
  };
  // Get claim method display name
  const getClaimMethodDisplay = () => {
    if (claimMethod === 'instant') return 'Instant';
    if (claimMethod === 'standard') return 'Standard';
    return '';
  };

  // Get title for modal header
  const getTitle = () => {
    switch (currentStep) {
      case STEPS.SELECT_TYPE:
        return 'Select Claim Type';
      case STEPS.SELECT_CLAIM_METHOD:
        return 'Select Claim Method';
      case STEPS.INSTANT_CLAIM_AMOUNT:
        return 'Instant Claim';
      case STEPS.STANDARD_CLAIM_AMOUNT:
        return 'Standard Claim';
      case STEPS.PAYMENT_METHOD:
        return 'Select Payment Method';
      case STEPS.MOBILE_MONEY_DETAILS:
        return 'Mobile Money Details';
      case STEPS.BANK_PAYMENT_DETAILS:
        return 'Bank Payment Details';
      case STEPS.REFUND_REQUEST:
        return 'Refund Request';
      case STEPS.REFUND_REVIEW:
        return 'Review Refund Request';
      case STEPS.SUMMARY:
        return 'Review Your Claim';
      case STEPS.SUCCESS:
        return 'Claim Submitted';
      case STEPS.INELIGIBLE:
        return 'Not Eligible';
      default:
        return 'Make a Claim';
    }
  };

  // Validate amount
  const handleAmountChange = (value) => {
    const sanitizedValue = value.replace(/[^0-9.]/g, '');
    const parts = sanitizedValue.split('.');
    const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitizedValue;
    const numValue = parseFloat(cleanValue) || 0;
    const maxClaimable = getMaxClaimable();

    if (numValue > maxClaimable) {
      setAmountError(`Amount cannot exceed maximum claimable of ${formatCurrency(maxClaimable)}`);
    } else if (numValue <= 0 && cleanValue !== '') {
      setAmountError('Amount must be greater than 0');
    } else {
      setAmountError('');
    }

    setClaimAmount(cleanValue);
  };

  // Handle phone number input
  const handlePhoneChange = (value) => {
    const normalized = normalizeGhanaPhone(value);
    setMomoWallet(normalized);

    if (normalized.length === 12) {
      const validation = validateGhanaPhone(normalized);
      setPhoneError(validation.error || '');
    } else if (normalized.length > 0 && normalized.length < 12) {
      setPhoneError('Phone number must be exactly 12 digits (233 + 9 digits)');
    } else {
      setPhoneError('');
    }
  };

  // Get selected telco name
  const getSelectedTelcoName = () => {
    const method = telcoMethods.find(m => m.emp_code === telecomMethod);
    return method ? toTitleCase(method.Name) : 'Mobile Money';
  };

  // Handle claim submission
  const handleSubmitClaim = () => {
    // Use refund claim API for refund claims
    if (claimType === 'refund_claim') {
      refundClaimMutation.mutate({
        policyNumber,
        currentCashValue,
        claimAmount,
        paymentMethod,
        telecomMethod,
        momoWallet,
        bankName: selectedBank,
        accountNumber,
        branchName: selectedBranch,
        accountName,
        refundReason,
      }, {
        onSuccess: (response) => {
          setClaimResponse(response?.data || {
            reference: 'SLAMS-' + Math.random().toString(36).substring(2, 10).toUpperCase(),
          });
          setCurrentStep(STEPS.SUCCESS);
          notifyUser('success', 'Your refund claim has been submitted successfully.');
        },
        onError: (error) => {
          notifyUser('error', formatValidationError(error));
        },
      });
    } else if (claimMethod === 'instant') {
      // Use instant claim API for instant claims
      instantClaimMutation.mutate({
        policyNumber,
        currentCashValue,
        claimAmount,
        withdrawalPurpose,
        telecomMethod,
        momoWallet,
      }, {
        onSuccess: (response) => {
          setClaimResponse(response?.data || {
            reference: 'SLAMS-' + Math.random().toString(36).substring(2, 10).toUpperCase(),
          });
          setCurrentStep(STEPS.SUCCESS);
          notifyUser('success', 'Your instant claim has been processed successfully.');
        },
        onError: (error) => {
          notifyUser('error', formatValidationError(error));
        },
      });
    } else {
      // Use standard claim API for standard claims
      standardClaimMutation.mutate({
        policyNumber,
        paymentMethod,
        currentCashValue,
        claimAmount,
        withdrawalPurpose,
        bankName: selectedBank,
        bankAccountNumber: accountNumber,
        branchName: selectedBranch,
        bankAccountName: accountName,
        telecomMethod,
        momoWallet,
      }, {
        onSuccess: (response) => {
          setClaimResponse(response?.data || {
            reference: 'SLAMS-' + Math.random().toString(36).substring(2, 10).toUpperCase(),
          });
          setCurrentStep(STEPS.SUCCESS);
          notifyUser('success', 'Your claim has been submitted successfully.');
        },
        onError: (error) => {
          notifyUser('error', formatValidationError(error));
        },
      });
    }
  };

  // Navigation handlers
  const handleSelectClaimType = async (type) => {
    if (type === 'partial_withdrawal') {
      try {
        const result = await checkEligibilityMutation.mutateAsync({ policyNumber });
        if (result?.success !== true) {
          const message = result?.message || 'You are not eligible for a partial withdrawal on this policy.';
          setIneligibilityMessage(message);
          setCurrentStep(STEPS.INELIGIBLE);
          return;
        }
        setClaimType(type);
        setCurrentStep(STEPS.SELECT_CLAIM_METHOD);
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Unable to verify eligibility. Please try again later.';
        setIneligibilityMessage(message);
        setCurrentStep(STEPS.INELIGIBLE);
      }
    } else {
      setClaimType(type);
      setCurrentStep(STEPS.REFUND_REQUEST);
    }
  };

  const handleSelectClaimMethod = (method) => {
    setClaimMethod(method);
    if (method === 'instant') {
      setCurrentStep(STEPS.INSTANT_CLAIM_AMOUNT);
    } else {
      // For standard claim - go to standard claim amount step
      setCurrentStep(STEPS.STANDARD_CLAIM_AMOUNT);
    }
  };
  // Format validation errors from API response (handles nested message.bank_name, etc.)
  const formatValidationError = (error) => {
    if (!error) return "An error occurred. Please try again.";
    if (error.message && typeof error.message === "object" && !Array.isArray(error.message)) {
      const messages = Object.entries(error.message).map(([field, errs]) => {
        if (Array.isArray(errs)) return errs.join(", ");
        return String(errs);
      });
      if (messages.length > 0) return messages.join("; ");
    }
    if (Array.isArray(error.message) && error.message.length > 0) {
      return error.message.join("; ");
    }
    if (error.message && typeof error.message === "string") {
      return error.message;
    }
    if (error.data && typeof error.data === "string") {
      return error.data;
    }
    return "An error occurred. Please try again.";
  };


  const handleContinueFromAmount = () => {
    const numValue = parseFloat(claimAmount) || 0;
    const maxClaimable = getMaxClaimable();

    if (numValue > maxClaimable) {
      notifyUser('error', `Amount cannot exceed maximum claimable of ${formatCurrency(maxClaimable)}`);
      return;
    }
    if (numValue <= 0) {
      notifyUser('error', 'Please enter a valid amount');
      return;
    }
    setCurrentStep(STEPS.MOBILE_MONEY_DETAILS);
  };

  const handleContinueFromMomo = () => {
    if (phoneError) {
      notifyUser('error', phoneError);
      return;
    }
    if (!momoWallet || momoWallet.length !== 12) {
      notifyUser('error', 'Please enter a valid phone number');
      return;
    }
    if (!telecomMethod) {
      notifyUser('error', 'Please select a telco operator');
      return;
    }
    setCurrentStep(STEPS.SUMMARY);
  };

  const handleBack = () => {
    switch (currentStep) {
      case STEPS.SELECT_CLAIM_METHOD:
      case STEPS.REFUND_REQUEST:
        setCurrentStep(STEPS.SELECT_TYPE);
        break;
      case STEPS.INSTANT_CLAIM_AMOUNT:
      case STEPS.STANDARD_CLAIM_AMOUNT:
        setCurrentStep(STEPS.SELECT_CLAIM_METHOD);
        break;
      case STEPS.PAYMENT_METHOD:
        setCurrentStep(STEPS.STANDARD_CLAIM_AMOUNT);
        break;
      case STEPS.MOBILE_MONEY_DETAILS:
        if (claimMethod === 'standard') {
          setCurrentStep(STEPS.PAYMENT_METHOD);
        } else {
          setCurrentStep(STEPS.INSTANT_CLAIM_AMOUNT);
        }
        break;
      case STEPS.BANK_PAYMENT_DETAILS:
        setCurrentStep(STEPS.PAYMENT_METHOD);
        break;
      case STEPS.REFUND_REVIEW:
        setCurrentStep(STEPS.REFUND_REQUEST);
        break;
      case STEPS.SUMMARY:
        if (paymentMethod === 'Bank') {
          setCurrentStep(STEPS.BANK_PAYMENT_DETAILS);
        } else {
          setCurrentStep(STEPS.MOBILE_MONEY_DETAILS);
        }
        break;
      case STEPS.INELIGIBLE:
        setCurrentStep(STEPS.SELECT_TYPE);
        break;
      default:
        break;
    }
  };

  const handleClose = () => {
    onClose();
  };

  // omk-text-field styles for error states
  const getTextFieldStyles = (hasError) => ({
    width: '100%',
    '--omk-textfield-height': '48px',
    '--omk-textfield-border-radius': '10px',
    '--omk-textfield-padding': '0 16px',
    '--omk-textfield-background': '#fff',
    '--omk-textfield-border-color': hasError ? '#ff4d4f' : '#ddd',
    '--omk-textfield-hover-border-color': '#0D7C4B',
  });

  // Render step: Ineligible
  const renderIneligible = () => (
    <div style={{ padding: '48px 32px', textAlign: 'center' }}>
      <div style={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        background: '#ff4d4f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
      }}>
        <span style={{ color: '#fff', fontSize: 32, fontWeight: 700, lineHeight: 1 }}>!</span>
      </div>
      <h3 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600, color: '#222' }}>
        Not Eligible
      </h3>
      <p style={{ margin: '0 0 24px', fontSize: 14, color: '#666', lineHeight: 1.6 }}>
        {ineligibilityMessage}
      </p>
      <button
        onClick={() => setCurrentStep(STEPS.SELECT_TYPE)}
        className="btn btn-primary"
        style={{ width: '100%' }}
      >
        Go Back
      </button>
    </div>
  );

  // Render step 1: Select Claim Type
  const renderSelectClaimType = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Partial Withdrawal Option */}
        <button
          onClick={() => handleSelectClaimType('partial_withdrawal')}
          disabled={checkEligibilityMutation.isLoading}
          style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: checkEligibilityMutation.isLoading ? 'not-allowed' : 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
            opacity: checkEligibilityMutation.isLoading ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 4 }}>
              {checkEligibilityMutation.isLoading ? 'Checking Eligibility...' : 'Partial Withdrawal'}
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              {checkEligibilityMutation.isLoading ? 'Please wait while we verify your eligibility.' : 'Withdrawal a portion of your cash value'}
            </div>
          </div>
          {checkEligibilityMutation.isLoading ? (
            <div style={{ width: 20, height: 20, border: '2px solid #e0e0e0', borderTopColor: '#0D7C4B', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          ) : (
            <ChevronRight size={20} color="#888" />
          )}
        </button>

        {/* Refund Claim Option */}
        <button
          onClick={() => {
            if (process.env.REACT_APP_REFUND_CLAIM_COMING_SOON_BUTTONS === 'true') {
              notifyUser('info', 'Coming Soon!', 'Info');
              return;
            }
            handleSelectClaimType('refund_claim');
          }}
          style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 4 }}>
              Refund Claim
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              Request reimbursement under policy guidelines
            </div>
          </div>
          <ChevronRight size={20} color="#888" />
        </button>
      </div>
    </div>
  );

  // Render step 2: Select Claim Method (Instant vs Standard)
  const renderSelectClaimMethod = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Instant Claims Option */}
        <button
          onClick={() => handleSelectClaimMethod('instant')}
          style={{ 
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 8 }}>
              Instant Claims
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Get funds immediately via mobile money
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>Max 40% of cash value (up to GH₵3,000)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>2% processing charge applies</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>Mobile money disbursement only</span>
              </div>
            </div>
          </div>
          <ChevronRight size={20} color="#888" style={{ marginTop: 4 }} />
        </button>

        {/* Standard Claim Option */}
        <button
          onClick={() => {
            // Temporarily redirect standard claims to the external claim request page.
            // Keeping in-app standard claim flow intact for later re-enable:
            // handleSelectClaimMethod('standard');
            window.open(
              "https://stak.oldmutual.com.gh/document/website/claim-request",
              "_blank",
              "noopener,noreferrer",
            );
          }}
          style={{ 
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 8 }}>
              Standard Claim
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Get funds within 5-7 business days
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>Max 50% of cash value (up to GH₵5,000)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>No monetary caps or charges</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>Mobile money or Bank Payment</span>
              </div>
            </div>
          </div>
          <ChevronRight size={20} color="#888" style={{ marginTop: 4 }} />
        </button>
      </div>
    </div>
  );

  // Render step 3: Instant Claim Amount
  const renderInstantClaimAmount = () => {
    const maxClaimable = getMaxClaimable();
    const withdrawalReasons = withdrawalReasonsData?.data || [];
    
    return (
      <div style={{ padding: '24px 32px 32px 32px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Amount Input */}
          <div>
            <omk-text-field
              label="Enter the amount you want to claim"
              name="claim_amount"
              type="text"
              value={claimAmount}
              onInput={(e) => handleAmountChange(e.target.value)}
              required
              style={getTextFieldStyles(amountError)}
              placeholder="Enter amount"
            />
            {amountError && (
              <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
                {amountError}
              </div>
            )}
          </div>

          {/* Withdrawal Purpose Dropdown */}
          <div>
            <omk-select
              label="Purpose of Withdrawal"
              name="withdrawal_purpose"
              value={withdrawalPurpose}
              onInput={(e) => setWithdrawalPurpose(e.target.value)}
              disabled={isLoadingWithdrawalReasons}
            >
              {isLoadingWithdrawalReasons ? (
                <omk-select-option value="">Loading...</omk-select-option>
              ) : withdrawalReasons.length > 0 ? (
                <>
                  <omk-select-option value="">Select purpose</omk-select-option>
                  {[...withdrawalReasons].sort((a, b) => {
                    const aText = (a.Description || a.description || a.name || a.label || '').toLowerCase();
                    const bText = (b.Description || b.description || b.name || b.label || '').toLowerCase();
                    return aText.localeCompare(bText);
                  }).map((reason) => (
                    <omk-select-option key={reason.id} value={reason.id}>
                      {reason.Description || reason.description || reason.name || reason.label}
                    </omk-select-option>
                  ))}
                </>
              ) : (
                <omk-select-option value="">No purposes available</omk-select-option>
              )}
            </omk-select>
          </div>

          {/* Info Display */}
          <div style={{ 
            background: '#f9f9f9', 
            borderRadius: 12, 
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Available cash value</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222' }}>
                {formatCurrency(currentCashValue)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Maximum claimable</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#0D7C4B' }}>
                {formatCurrency(maxClaimable)}
              </span>
            </div>
          </div>

          {/* Continue Button */}
          <button
            onClick={handleContinueFromAmount}
            className="btn btn-primary"
            disabled={!claimAmount || amountError || !withdrawalPurpose}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  // Render step: Standard Claim Amount
  const renderStandardClaimAmount = () => {
    const maxClaimable = getMaxClaimable();
    const withdrawalReasons = withdrawalReasonsData?.data || [];
    
    return (
      <div style={{ padding: '24px 32px 32px 32px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Amount Input */}
          <div>
            <omk-text-field
              label="Enter the amount you want to claim"
              name="claim_amount"
              type="text"
              value={claimAmount}
              onInput={(e) => handleAmountChange(e.target.value)}
              required
              style={getTextFieldStyles(amountError)}
              placeholder="Enter amount"
            />
            {amountError && (
              <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
                {amountError}
              </div>
            )}
          </div>

          {/* Withdrawal Purpose Dropdown */}
          <div>
            <omk-select
              label="Purpose of Withdrawal"
              name="withdrawal_purpose"
              value={withdrawalPurpose}
              onInput={(e) => setWithdrawalPurpose(e.target.value)}
              disabled={isLoadingWithdrawalReasons}
            >
              {isLoadingWithdrawalReasons ? (
                <omk-select-option value="">Loading...</omk-select-option>
              ) : withdrawalReasons.length > 0 ? (
                <>
                  <omk-select-option value="">Select purpose</omk-select-option>
                  {[...withdrawalReasons].sort((a, b) => {
                    const aText = (a.Description || a.description || a.name || a.label || '').toLowerCase();
                    const bText = (b.Description || b.description || b.name || b.label || '').toLowerCase();
                    return aText.localeCompare(bText);
                  }).map((reason) => (
                    <omk-select-option key={reason.id} value={reason.id}>
                      {reason.Description || reason.description || reason.name || reason.label}
                    </omk-select-option>
                  ))}
                </>
              ) : (
                <omk-select-option value="">No purposes available</omk-select-option>
              )}
            </omk-select>
          </div>

          {/* Info Display */}
          <div style={{ 
            background: '#f9f9f9', 
            borderRadius: 12, 
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Available cash value</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222' }}>
                {formatCurrency(currentCashValue)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Maximum claimable</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#0D7C4B' }}>
                {formatCurrency(maxClaimable)}
              </span>
            </div>
          </div>

          {/* Continue Button */}
          <button
            onClick={() => {
              const numValue = parseFloat(claimAmount) || 0;
              const maxClaimable = getMaxClaimable();
              if (numValue > maxClaimable) {
                notifyUser('error', `Amount cannot exceed maximum claimable of ${formatCurrency(maxClaimable)}`);
                return;
              }
              if (numValue <= 0) {
                notifyUser('error', 'Please enter a valid amount');
                return;
              }
              if (!withdrawalPurpose) {
                notifyUser('error', 'Please select a purpose of withdrawal');
                return;
              }
              setCurrentStep(STEPS.PAYMENT_METHOD);
            }}
            className="btn btn-primary"
            disabled={!claimAmount || amountError || !withdrawalPurpose}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  // Render step: Payment Method Selection
  const renderPaymentMethod = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Mobile Money Option */}
        <button
          onClick={() => {
            setPaymentMethod('Mobile Money');
            setCurrentStep(STEPS.MOBILE_MONEY_DETAILS);
          }}
          style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 4 }}>
              Mobile Money
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              Withdrawal to your phone number
            </div>
          </div>
          <ChevronRight size={20} color="#888" />
        </button>

        {/* Bank Payment Option */}
        <button
          onClick={() => {
            setPaymentMethod('Bank');
            setCurrentStep(STEPS.BANK_PAYMENT_DETAILS);
          }}
          style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 4 }}>
              Bank Payment
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              Withdrawal to your local bank account
            </div>
          </div>
          <ChevronRight size={20} color="#888" />
        </button>
      </div>
    </div>
  );

  // Render step: Bank Payment Details
  const renderBankPaymentDetails = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Bank Dropdown */}
        <div>
          <omk-select
            label="Select bank"
            name="bank"
            value={selectedBank}
            onInput={(e) => setSelectedBank(e.target.value)}
          >
            <omk-select-option value="">Select bank</omk-select-option>
            <omk-select-option value="ecobank">Ecobank Ghana</omk-select-option>
            <omk-select-option value="gtbank">GTBank Ghana</omk-select-option>
            <omk-select-option value="fidelity">Fidelity Bank</omk-select-option>
            <omk-select-option value="calbank">CalBank</omk-select-option>
            <omk-select-option value="gcb">GCB Bank</omk-select-option>
            <omk-select-option value="absa">Absa Bank</omk-select-option>
            <omk-select-option value="standard_chartered">Standard Chartered</omk-select-option>
            <omk-select-option value="uba">UBA Ghana</omk-select-option>
            <omk-select-option value="zenith">Zenith Bank</omk-select-option>
          </omk-select>
        </div>

        {/* Branch Input */}
        <div>
          <omk-text-field
            label="Branch name"
            name="branch"
            type="text"
            value={selectedBranch}
            onInput={(e) => setSelectedBranch(e.target.value)}
            disabled={!selectedBank}
            required={selectedBank !== ''}
            style={getTextFieldStyles(false)}
            placeholder="Enter branch name"
          />
        </div>

        {/* Account Number Input */}
        <div>
          <omk-text-field
            label="Enter account number"
            name="account_number"
            type="text"
            value={accountNumber}
            onInput={(e) => setAccountNumber(e.target.value.replace(/[^0-9]/g, ''))}
            required
            style={getTextFieldStyles(false)}
            placeholder="Enter account number"
          />
        </div>

        {/* Account Name Input -->
        <div>
          <omk-text-field
            label="Account Name"
            name="account_name"
            type="text"
            value={accountName}
            onInput={(e) => setAccountName(e.target.value)}
            style={{
              ...getTextFieldStyles(false),
              '--omk-textfield-background': '#f5f5f5',
            }}
            placeholder="Account name will appear here"
          />
        </div>

        {/* Continue Button */}
        <button
          onClick={() => {
            if (!selectedBank) {
              notifyUser('error', 'Please select a bank');
              return;
            }
            if (!selectedBranch) {
              notifyUser('error', 'Please select a branch');
              return;
            }
            if (!accountNumber || accountNumber.length < 10) {
              notifyUser('error', 'Please enter a valid account number');
              return;
            }
            setCurrentStep(STEPS.SUMMARY);
          }}
          className="btn btn-primary"
          disabled={!selectedBank || !selectedBranch || !accountNumber}
        >
          Continue
        </button>
      </div>
    </div>
  );

  // Render step: Mobile Money Details
  const renderMobileMoneyDetails = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Telco Operator Dropdown */}
        <div>
          <omk-select
            label="Select Telco Operator"
            name="telco_method"
            value={telecomMethod}
            onInput={(e) => setTelecomMethod(e.target.value)}
            disabled={isLoadingTelcoMethods}
          >
            {isLoadingTelcoMethods ? (
              <omk-select-option value="">Loading...</omk-select-option>
            ) : telcoMethods.length > 0 ? (
              telcoMethods.map((method) => (
                <omk-select-option key={method.emp_code} value={method.emp_code}>
                  {toTitleCase(method.Name)}
                </omk-select-option>
              ))
            ) : (
              <omk-select-option value="">No payment methods available</omk-select-option>
            )}
          </omk-select>
        </div>

        {/* Phone Number Input */}
        <div>
          <omk-text-field
            label="Registered Phone Number"
            name="momo_wallet"
            type="tel"
            value={momoWallet}
            onInput={(e) => handlePhoneChange(e.target.value)}
            required
            style={getTextFieldStyles(phoneError)}
            placeholder="05XXXXXXXX or 233XXXXXXXXX"
            maxLength="12"
            pattern="[0-9]*"
            inputMode="numeric"
          />
          {phoneError && (
            <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
              {phoneError}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div style={{
          background: '#FFF8E7',
          border: '1px solid #FFE4B5',
          borderRadius: 10,
          padding: '16px',
        }}>
          <p style={{ margin: 0, fontSize: 13, color: '#8B7355', lineHeight: 1.5 }}>
            Identity validation will be performed via Hubtel. At least two names must match in any order.
          </p>
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinueFromMomo}
          className="btn btn-primary"
          disabled={!momoWallet || phoneError || !telecomMethod}
        >
          Continue
        </button>
      </div>
    </div>
  );

  // Render step: Refund Request
  const renderRefundRequest = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Manual Processing Warning */}
        <div style={{
          background: '#FFF8E7',
          border: '1px solid #FFE4B5',
          borderRadius: 10,
          padding: '16px',
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 600, color: '#8B7355' }}>
            Manual Processing
          </p>
          <p style={{ margin: 0, fontSize: 13, color: '#8B7355', lineHeight: 1.5 }}>
            Refund claims are reviewed by the claims operations team. You may be contacted for supporting documentation.
          </p>
        </div>

        {/* Refund Amount Input */}
        <div>
          <omk-text-field
            label="Refund Amount"
            name="refund_amount"
            type="text"
            value={claimAmount}
            onInput={(e) => handleAmountChange(e.target.value)}
            required
            style={getTextFieldStyles(amountError)}
            placeholder="Enter amount"
          />
          {amountError && (
            <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
              {amountError}
            </div>
          )}
        </div>

        {/* Reason Textarea - using standard textarea since omk-text-field doesn't support multiline */}
        <div>
          <label style={{ display: 'block', fontSize: 14, color: '#666', marginBottom: 8 }}>
            Describe the reason for refund request
          </label>
          <textarea
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder="Enter your reason here..."
            rows={4}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #ddd',
              borderRadius: 10,
              fontSize: 15,
              outline: 'none',
              resize: 'none',
              fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Continue Button */}
        <button
          onClick={() => {
            const numValue = parseFloat(claimAmount) || 0;
            if (numValue <= 0) {
              notifyUser('error', 'Please enter a valid amount');
              return;
            }
            if (!refundReason.trim()) {
              notifyUser('error', 'Please provide a reason for the refund request');
              return;
            }
            setCurrentStep(STEPS.REFUND_REVIEW);
          }}
          className="btn btn-primary"
          disabled={!claimAmount || amountError || !refundReason.trim()}
        >
          Continue
        </button>
      </div>
    </div>
  );

  // Render step: Refund Review
  const renderRefundReview = () => {
    const isSubmitting = refundClaimMutation.isLoading;
    
    return (
      <div style={{ padding: '24px 32px 32px 32px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Review Details */}
          <div style={{ 
            background: '#f9f9f9', 
            borderRadius: 12, 
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Policy</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222', textAlign: 'right' }}>
                {policyName || 'SmartLife Investment Plan (SIP)'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Claim Type</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222' }}>
                Refund Claim
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Refund Amount</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>
                {formatCurrency(parseFloat(claimAmount) || 0)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Reason</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222', textAlign: 'right', maxWidth: '60%' }}>
                {refundReason}
              </span>
            </div>
          </div>

          {/* Disclaimer */}
          <p style={{ margin: 0, fontSize: 12, color: '#888', lineHeight: 1.5, textAlign: 'center' }}>
            By submitting, your refund claim will be sent to SLAMS for review and processing by a claims officer.
            You may be contacted for additional documentation.
          </p>

          {/* Submit Button */}
          <button
            onClick={() => {
              handleSubmitClaim();
            }}
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Refund Claim'}
          </button>
        </div>
      </div>
    );
  };

  // Render step: Summary
  const renderSummary = () => {
    // Get payment display info
    const getPaymentDisplay = () => {
      if (paymentMethod === 'Bank') {
        const bankNames = {
          ecobank: 'Ecobank Ghana',
          gtbank: 'GTBank Ghana',
          fidelity: 'Fidelity Bank',
          calbank: 'CalBank',
          gcb: 'GCB Bank',
          absa: 'Absa Bank',
          standard_chartered: 'Standard Chartered',
          uba: 'UBA Ghana',
          zenith: 'Zenith Bank',
        };
        return `${bankNames[selectedBank] || selectedBank} — ${accountNumber}`;
      }
      return `${getSelectedTelcoName()} — ${momoWallet}`;
    };

    // Get withdrawal purpose display name
    const getWithdrawalPurposeDisplay = () => {
      const reasons = withdrawalReasonsData?.data || [];
      const selectedReason = reasons.find(r => r.id === withdrawalPurpose || r.id === parseInt(withdrawalPurpose));
      return selectedReason?.Description || selectedReason?.description || selectedReason?.name || selectedReason?.label || 'N/A';
    };

    const isSubmitting = claimRequestMutation.isLoading || instantClaimMutation.isLoading || refundClaimMutation.isLoading;

    return (
      <div style={{ padding: '24px 32px 32px 32px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Summary Details */}
          <div style={{ 
            background: '#f9f9f9', 
            borderRadius: 12, 
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Policy</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222', textAlign: 'right' }}>
                {policyName || 'SmartLife Investment Plan (SIP)'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Claim Type</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222' }}>
                Partial Withdrawal — {getClaimMethodDisplay()}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Claim Amount</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>
                {formatCurrency(parseFloat(claimAmount) || 0)}
              </span>
            </div>
            {(claimMethod === 'instant' || claimMethod === 'standard') && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: '#666' }}>Purpose</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#222', textAlign: 'right' }}>
                  {getWithdrawalPurposeDisplay()}
                </span>
              </div>
            )}
          </div>

          <div style={{ 
            background: '#f9f9f9', 
            borderRadius: 12, 
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Payment Method</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222', textAlign: 'right' }}>
                {paymentMethod === 'Bank' ? 'Bank Payment' : 'Mobile Money'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Payment Details</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222', textAlign: 'right' }}>
                {getPaymentDisplay()}
              </span>
            </div>
          </div>

          {/* Disclaimer */}
          <p style={{ margin: 0, fontSize: 12, color: '#888', lineHeight: 1.5 }}>
            {claimMethod === 'instant' 
              ? 'By submitting, your claim will be processed immediately through Hubtel and a reference generated via SLAMS.'
              : 'By submitting, your claim will be sent to SLAMS for review and processing by a claims officer.'}
          </p>

          {/* Process Claim Button */}
          <button
             onClick={() => {
               handleSubmitClaim();
             }}
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Process Claim'}
          </button>
        </div>
      </div>
    );
  };

  // Render step: Success
  const renderSuccess = () => (
    <div style={{ padding: '48px 32px', textAlign: 'center' }}>
      <div style={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        background: '#0D7C4B',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
      }}>
        <Check size={32} color="#fff" />
      </div>
      <h3 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 600, color: '#222' }}>
        Claim Submitted Successfully!
      </h3>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: '#666' }}>
        Your claim has been received and is being processed.
      </p>
      {claimResponse?.reference && (
        <div style={{
          background: '#f5f5f5',
          borderRadius: 8,
          padding: '12px',
          marginBottom: 24,
        }}>
          <p style={{ margin: 0, fontSize: 12, color: '#666' }}>Reference Number</p>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#222' }}>
            {claimResponse.reference}
          </p>
        </div>
      )}
      <button
        onClick={handleClose}
        className="btn btn-primary"
        style={{ width: '100%' }}
      >
        Close
      </button>
    </div>
  );

  // Render current step content
  const renderContent = () => {
    switch (currentStep) {
      case STEPS.SELECT_TYPE:
        return renderSelectClaimType();
      case STEPS.SELECT_CLAIM_METHOD:
        return renderSelectClaimMethod();
      case STEPS.INSTANT_CLAIM_AMOUNT:
        return renderInstantClaimAmount();
      case STEPS.STANDARD_CLAIM_AMOUNT:
        return renderStandardClaimAmount();
      case STEPS.PAYMENT_METHOD:
        return renderPaymentMethod();
      case STEPS.MOBILE_MONEY_DETAILS:
        return renderMobileMoneyDetails();
      case STEPS.BANK_PAYMENT_DETAILS:
        return renderBankPaymentDetails();
      case STEPS.REFUND_REQUEST:
        return renderRefundRequest();
      case STEPS.REFUND_REVIEW:
        return renderRefundReview();
      case STEPS.SUMMARY:
        return renderSummary();
      case STEPS.SUCCESS:
        return renderSuccess();
      case STEPS.INELIGIBLE:
        return renderIneligible();
      default:
        return renderSelectClaimType();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="claims-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.18)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          .claims-modal-overlay {
            align-items: flex-start !important;
            padding-top: 40px !important;
          }
        }
      `}</style>
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: 420,
          maxHeight: '85vh',
          overflowY: 'auto',
          padding: 0,
          boxShadow: '0 16px 60px rgba(0,0,0,0.15)'
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        {currentStep !== STEPS.SUCCESS && (
          <div style={{ padding: '24px 32px 0 32px' }}>
            <div 
              className="modal-header-title" 
              style={{ 
                fontWeight: 700, 
                color: '#222', 
                fontSize: 18,
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between' 
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {currentStep !== STEPS.SELECT_TYPE && (
                  <span
                    onClick={handleBack}
                    style={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#666',
                    }}
                  >
                    <ChevronRight size={20} style={{ transform: 'rotate(180deg)' }} />
                  </span>
                )}
                <span>{getTitle()}</span>
              </div>
              <span 
                onClick={onClose} 
                style={{ 
                  cursor: 'pointer',
                  fontSize: 24,
                  fontWeight: 300,
                  color: '#888',
                  lineHeight: 1,
                }} 
                aria-label="Close"
              >
                ×
              </span>
            </div>
          </div>
        )}

        {/* Content */}
        {renderContent()}
      </div>
    </div>
  );
};

export default ClaimsModal;
